// Lazy-load Shiki and highlight remaining <pre><code class="language-xxx"> blocks.
// Mermaid blocks should have already been consumed by renderMermaidIn before this runs.

import type { Highlighter } from "shiki";

let highlighterPromise: Promise<Highlighter> | null = null;
const loadedLangs = new Set<string>(["text"]);

async function getHighlighter(): Promise<Highlighter> {
  if (!highlighterPromise) {
    highlighterPromise = (async () => {
      const { createHighlighter } = await import("shiki");
      return createHighlighter({
        themes: ["github-light", "github-dark"],
        langs: [],
      });
    })();
  }
  return highlighterPromise;
}

function currentTheme(): "github-light" | "github-dark" {
  return document.documentElement.dataset.theme === "dark" ? "github-dark" : "github-light";
}

async function ensureLang(hl: Highlighter, lang: string): Promise<string> {
  if (loadedLangs.has(lang)) return lang;
  try {
    await hl.loadLanguage(lang as never);
    loadedLangs.add(lang);
    return lang;
  } catch {
    return "text";
  }
}

function renderBlock(hl: Highlighter, source: string, lang: string, theme: string): HTMLElement | null {
  const html = hl.codeToHtml(source, { lang, theme });
  const tpl = document.createElement("template");
  tpl.innerHTML = html.trim();
  const pre = tpl.content.firstElementChild as HTMLElement | null;
  if (pre) {
    pre.dataset.shikiSource = source;
    pre.dataset.shikiLang = lang;
  }
  return pre;
}

export async function highlightIn(root: HTMLElement): Promise<void> {
  const blocks = Array.from(
    root.querySelectorAll<HTMLElement>("pre > code[class*='language-']")
  ).filter((c) => !c.classList.contains("language-mermaid") && !c.classList.contains("lang-mermaid"));
  if (blocks.length === 0) return;

  const hl = await getHighlighter();
  const theme = currentTheme();

  for (const code of blocks) {
    const pre = code.parentElement;
    if (!pre) continue;
    const requested = (code.className.match(/language-([\w+-]+)/)?.[1] ?? "text").toLowerCase();
    const lang = await ensureLang(hl, requested);
    const newPre = renderBlock(hl, code.textContent ?? "", lang, theme);
    if (newPre) pre.replaceWith(newPre);
  }
}

export async function rehighlightAll(root: HTMLElement): Promise<void> {
  const blocks = Array.from(
    root.querySelectorAll<HTMLElement>("pre.shiki[data-shiki-source]")
  );
  if (blocks.length === 0) return;

  const hl = await getHighlighter();
  const theme = currentTheme();

  for (const pre of blocks) {
    const source = pre.dataset.shikiSource ?? "";
    const requested = (pre.dataset.shikiLang ?? "text").toLowerCase();
    const lang = await ensureLang(hl, requested);
    const newPre = renderBlock(hl, source, lang, theme);
    if (newPre) pre.replaceWith(newPre);
  }
}
